import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Link as LinkIcon, Edit2, ExternalLink } from "lucide-react";
import { useSavedLinks, SavedLink } from "@/hooks/useSavedLinks";
import { useAuth } from "@/hooks/useAuth";

const LINK_TYPES = [
  { value: "linkedin", label: "LinkedIn" },
  { value: "github", label: "GitHub" },
  { value: "portfolio", label: "Portfolio" },
  { value: "project", label: "Project" },
  { value: "other", label: "Other" },
];

export const SavedLinksManager = () => {
  const { user } = useAuth();
  const { links, isLoading, addLink, updateLink, deleteLink } = useSavedLinks();
  const [isOpen, setIsOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<SavedLink | null>(null);
  const [formData, setFormData] = useState({
    link_type: "linkedin",
    label: "",
    url: "",
  });

  const resetForm = () => {
    setFormData({ link_type: "linkedin", label: "", url: "" });
    setEditingLink(null);
  };

  const handleSubmit = async () => {
    if (!formData.label.trim() || !formData.url.trim()) return;

    if (editingLink) {
      await updateLink(editingLink.id, formData);
    } else {
      await addLink(formData.link_type, formData.label.trim(), formData.url.trim());
    }
    resetForm();
  };

  const handleEdit = (link: SavedLink) => {
    setEditingLink(link);
    setFormData({
      link_type: link.link_type,
      label: link.label,
      url: link.url,
    });
  };

  const handleDelete = async (id: string) => {
    await deleteLink(id);
  };

  if (!user) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <LinkIcon className="w-4 h-4" />
          Saved Links
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5" />
            Manage Saved Links
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Add/Edit Form */}
          <Card className="p-4 bg-muted/30">
            <h4 className="font-medium mb-3">
              {editingLink ? "Edit Link" : "Add New Link"}
            </h4>
            <div className="space-y-3">
              <div className="grid gap-2">
                <Label className="text-xs">Type</Label>
                <Select
                  value={formData.link_type}
                  onValueChange={(v) => setFormData((prev) => ({ ...prev, link_type: v }))}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[100] bg-background">
                    {LINK_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs">Label (display name)</Label>
                <Input
                  placeholder="My LinkedIn"
                  value={formData.label}
                  onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs">URL</Label>
                <Input
                  placeholder="https://linkedin.com/in/yourname"
                  value={formData.url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSubmit}
                  disabled={!formData.label.trim() || !formData.url.trim()}
                  className="flex-1"
                >
                  {editingLink ? "Update" : "Save Link"}
                </Button>
                {editingLink && (
                  <Button variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Links List */}
          <div className="space-y-2">
            <h4 className="font-medium">Your Saved Links</h4>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : links.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No saved links yet. Add your LinkedIn, GitHub, or portfolio links above.
              </p>
            ) : (
              <div className="space-y-2">
                {links.map((link) => (
                  <div
                    key={link.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded capitalize">
                          {link.link_type}
                        </span>
                        <span className="font-medium truncate">{link.label}</span>
                      </div>
                      <a
                        href={link.url.startsWith("http") ? link.url : `https://${link.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-muted-foreground hover:text-primary truncate flex items-center gap-1"
                      >
                        {link.url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(link)}
                        className="h-8 w-8"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(link.id)}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
